import { createContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { axiosPost } from "../endpoints/axios";

const AuthContext = createContext();

export default AuthContext;

function setUserObject(user) {
  if (!user) return null;
  return {
    username: user.username,
    id: user.user_id,
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
  };
}

export const AuthProvider = ({ children }) => {
  const BASE_URL = "https://rezin.pythonanywhere.com/auth";
  const userToken = JSON.parse(localStorage.getItem("token"));
  const decodedUser = userToken ? jwtDecode(userToken) : null;

  const [token, setToken] = useState(userToken);
  const [user, setUser] = useState(setUserObject(decodedUser));
  const [isServerError, setIsServerError] = useState(false);
  const navigate = useNavigate();

  // Track if we already sent the activity for this component mount
  const hasTracked = useRef(false);

  const trackActivity = useCallback(
    async (authToken) => {
      if (!authToken) return;

      // Safety Check: Don't call if the token is expired
      try {
        const decoded = jwtDecode(authToken);
        const currentTime = Date.now() / 1000;
        if (decoded.exp && decoded.exp < currentTime) {
          console.warn("Token is expired. Skipping activity log.");
          return;
        }
      } catch (e) {
        console.warn("Invalid token format. Skipping activity log.");
        return;
      }

      try {
        await axiosPost(`${BASE_URL}/track-activity/`, {}, authToken);
        hasTracked.current = true; // Mark as successful
      } catch (error) {
        console.warn("Daily activity could not be logged.");
        // If it fails, stop retrying to prevent network spam
        hasTracked.current = true;
      }
    },
    [BASE_URL],
  );

  // Auto-track activity safely on app load
  useEffect(() => {
    if (token && !hasTracked.current) {
      trackActivity(token);
    }
  }, [token, trackActivity]);

  const registerUser = async (registerData) => {
    try {
      let finalData = {
        username: registerData.username,
        password: registerData.password,
        email: registerData.email,
        first_name: registerData.firstName,
        last_name: registerData.lastName,
      };
      await axios.post(`${BASE_URL}/register/`, finalData);
      alert("Congrats! You are now registered.");
      navigate("/login");
      setIsServerError(false);
    } catch (error) {
      alert(error.message);
      console.log(error.message);
    }
  };

  const loginUser = async (loginData) => {
    try {
      let response = await axios.post(`${BASE_URL}/login/`, loginData);
      if (response.status === 200) {
        const accessToken = response.data.access;
        localStorage.setItem("token", JSON.stringify(accessToken));

        // Reset the tracker flag for the new valid login session
        hasTracked.current = false;
        setToken(accessToken);

        let loggedInUser = jwtDecode(accessToken);
        setUser(setUserObject(loggedInUser));
        setIsServerError(false);

        trackActivity(accessToken);

        navigate("/dashboard");
      }
    } catch (error) {
      setIsServerError(true);
      alert("Invalid credentials");
    }
  };

  const logoutUser = () => {
    if (user) {
      localStorage.removeItem("token");
      setUser(null);
      setToken(null);
      hasTracked.current = false; // Reset on logout
      navigate("/");
    }
  };

  const contextData = {
    user,
    token,
    loginUser,
    logoutUser,
    registerUser,
    isServerError,
  };

  return (
    <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>
  );
};
